import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, CalendarDays, User, Plane, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const LeaveApproval = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const preAction = params.get('action');

  const [loading, setLoading] = useState(true);
  const [req, setReq] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(preAction === 'reject' ? 'reject' : preAction === 'approve' ? 'approve' : null);
  const [reason, setReason] = useState('');
  const [decidedBy, setDecidedBy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // 'approved' | 'rejected'

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('No approval token provided.');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/public/leave-approval/${token}`);
        setReq(res.data);
        if (res.data.status !== 'pending') {
          setResult(res.data.status);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || 'This approval link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const submitDecision = async (action) => {
    if (action === 'reject' && !reason.trim()) {
      setError('Please provide a reason for rejection.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/public/leave-approval/${token}/decide`, {
        action,
        reason: reason.trim(),
        decided_by: decidedBy.trim(),
      });
      setResult(res.data.status);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not record your decision. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="leave-approval-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="bg-indigo-600 px-6 py-5">
          <h1 className="text-white text-lg font-semibold">Leave Approval</h1>
          <p className="text-indigo-200 text-xs mt-0.5">Vynce HR</p>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading request…
        </div>
      </Shell>
    );
  }

  if (error && !req) {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="leave-approval-error">
          <XCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">{error}</p>
        </div>
      </Shell>
    );
  }

  const Details = () => (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2 text-sm" data-testid="leave-approval-details">
      <div className="flex items-center gap-2 text-slate-800 font-semibold">
        <User className="h-4 w-4 text-indigo-500" />
        {req.employee_name} {req.employee_code ? <span className="text-slate-400 font-normal">({req.employee_code})</span> : null}
      </div>
      <div className="flex items-center gap-2 text-slate-600">
        <CalendarDays className="h-4 w-4 text-indigo-500" />
        {fmtDate(req.start_date)} → {fmtDate(req.end_date)}
        <span className="text-slate-400">· {req.days} day(s)</span>
      </div>
      <div className="text-slate-600"><span className="font-medium">Type:</span> {req.leave_type_label}</div>
      {req.department ? <div className="text-slate-600"><span className="font-medium">Department:</span> {req.department}</div> : null}
      {req.reason ? <div className="text-slate-600"><span className="font-medium">Reason:</span> {req.reason}</div> : null}
      {req.flight_ticket_provided ? (
        <div className="flex items-center gap-2 text-emerald-700"><Plane className="h-4 w-4" /> Flight ticket provided</div>
      ) : null}
      <div className="text-xs text-slate-400 pt-1">Requested by {req.requested_by} · {fmtDate(req.requested_at)}</div>
    </div>
  );

  // Already decided (either before or just now)
  if (result) {
    const approved = result === 'approved';
    return (
      <Shell>
        <div className="text-center" data-testid="leave-approval-result">
          {approved ? (
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
          ) : (
            <XCircle className="h-14 w-14 text-rose-500 mx-auto mb-3" />
          )}
          <h2 className={`text-xl font-bold ${approved ? 'text-emerald-600' : 'text-rose-600'}`}>
            Leave {approved ? 'Approved' : 'Rejected'}
          </h2>
          <p className="text-slate-500 text-sm mt-1 mb-4">
            {approved ? 'The leave has been recorded and HR notified.' : 'HR has been notified of your decision.'}
          </p>
          <Details />
          {req.decided_by ? (
            <p className="text-xs text-slate-400 mt-3">
              Decided by {req.decided_by} · {fmtDate(req.decided_at)}
            </p>
          ) : null}
          {!approved && req.decision_reason ? (
            <p className="text-sm text-rose-600 mt-2">Reason: {req.decision_reason}</p>
          ) : null}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-amber-600 text-sm font-medium">
          <Clock className="h-4 w-4" /> Awaiting your decision
        </div>
        <Details />

        {error ? <p className="text-sm text-rose-600" data-testid="leave-approval-inline-error">{error}</p> : null}

        <div>
          <label className="text-xs font-medium text-slate-600">Your name (optional)</label>
          <Input
            value={decidedBy}
            onChange={(e) => setDecidedBy(e.target.value)}
            placeholder="e.g. Ahmed (Manager)"
            data-testid="leave-approval-decided-by"
            className="mt-1"
          />
        </div>

        {mode === 'reject' && (
          <div>
            <label className="text-xs font-medium text-slate-600">Reason for rejection <span className="text-rose-500">*</span></label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Let HR know why this leave is being rejected…"
              rows={3}
              data-testid="leave-approval-reason"
              className="mt-1"
            />
          </div>
        )}

        {mode === 'reject' ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { setMode(null); setError(''); }}
              disabled={submitting}
              data-testid="leave-approval-back-btn"
            >
              Back
            </Button>
            <Button
              className="flex-1 bg-rose-600 hover:bg-rose-700"
              onClick={() => submitDecision('reject')}
              disabled={submitting}
              data-testid="leave-approval-confirm-reject-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Rejection'}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => submitDecision('approve')}
              disabled={submitting}
              data-testid="leave-approval-approve-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</>)}
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50"
              onClick={() => { setMode('reject'); setError(''); }}
              disabled={submitting}
              data-testid="leave-approval-reject-btn"
            >
              <XCircle className="h-4 w-4 mr-1" /> Reject
            </Button>
          </div>
        )}
        <p className="text-[11px] text-slate-400 text-center">Secure one-click approval · no login required</p>
      </div>
    </Shell>
  );
};

export default LeaveApproval;
